/**
 * Device model resolution — turns the raw signals captured at login
 * (User-Agent, parsed model code, screen signature, iOS version…) into a
 * human-readable device name such as "iPhone 17 Pro Max" or
 * "Samsung Galaxy S21 Ultra".
 *
 * Runs purely on the admin side; nothing here needs the casino client or the
 * login lambda to change. Best effort by design:
 *
 *  - Android: the UA / client hints carry the model *code* (SM-G998B). We map
 *    the codes we care about (Samsung, Huawei/Honor, Xiaomi, OPPO/realme…) to
 *    marketing names. Unknown codes are shown as "<Vendor> <code>".
 *  - iPhone / iPad: Safari never exposes the model. We use, in order:
 *      1. an Apple hardware identifier leaked into the UA by in-app browsers
 *         (Facebook / Instagram / Messenger add `FBDV/iPhone18,2`) → exact;
 *      2. the CSS screen signature (points × devicePixelRatio) narrowed by the
 *         iOS major version → a short list of physically identical models.
 *
 * Confidence levels returned:
 *   'exact'    – hardware identifier matched
 *   'code'     – Android model code / marketing name from the UA
 *   'inferred' – Apple screen signature (candidates.length may be > 1)
 *   'unknown'  – we only know the family (iPhone / iPad / vendor)
 */

// ---------------------------------------------------------------------------
// Apple — hardware identifiers (leaked by FB/IG/Messenger in-app browsers)
// ---------------------------------------------------------------------------

export const APPLE_HARDWARE_IDS = {
  'iPhone8,1': 'iPhone 6s',
  'iPhone8,2': 'iPhone 6s Plus',
  'iPhone8,4': 'iPhone SE (1st gen)',
  'iPhone9,1': 'iPhone 7',
  'iPhone9,3': 'iPhone 7',
  'iPhone9,2': 'iPhone 7 Plus',
  'iPhone9,4': 'iPhone 7 Plus',
  'iPhone10,1': 'iPhone 8',
  'iPhone10,4': 'iPhone 8',
  'iPhone10,2': 'iPhone 8 Plus',
  'iPhone10,5': 'iPhone 8 Plus',
  'iPhone10,3': 'iPhone X',
  'iPhone10,6': 'iPhone X',
  'iPhone11,2': 'iPhone XS',
  'iPhone11,4': 'iPhone XS Max',
  'iPhone11,6': 'iPhone XS Max',
  'iPhone11,8': 'iPhone XR',
  'iPhone12,1': 'iPhone 11',
  'iPhone12,3': 'iPhone 11 Pro',
  'iPhone12,5': 'iPhone 11 Pro Max',
  'iPhone12,8': 'iPhone SE (2nd gen)',
  'iPhone13,1': 'iPhone 12 mini',
  'iPhone13,2': 'iPhone 12',
  'iPhone13,3': 'iPhone 12 Pro',
  'iPhone13,4': 'iPhone 12 Pro Max',
  'iPhone14,4': 'iPhone 13 mini',
  'iPhone14,5': 'iPhone 13',
  'iPhone14,2': 'iPhone 13 Pro',
  'iPhone14,3': 'iPhone 13 Pro Max',
  'iPhone14,6': 'iPhone SE (3rd gen)',
  'iPhone14,7': 'iPhone 14',
  'iPhone14,8': 'iPhone 14 Plus',
  'iPhone15,2': 'iPhone 14 Pro',
  'iPhone15,3': 'iPhone 14 Pro Max',
  'iPhone15,4': 'iPhone 15',
  'iPhone15,5': 'iPhone 15 Plus',
  'iPhone16,1': 'iPhone 15 Pro',
  'iPhone16,2': 'iPhone 15 Pro Max',
  'iPhone17,3': 'iPhone 16',
  'iPhone17,4': 'iPhone 16 Plus',
  'iPhone17,1': 'iPhone 16 Pro',
  'iPhone17,2': 'iPhone 16 Pro Max',
  'iPhone17,5': 'iPhone 16e',
  'iPhone18,1': 'iPhone 17 Pro',
  'iPhone18,2': 'iPhone 17 Pro Max',
  'iPhone18,3': 'iPhone 17',
  'iPhone18,4': 'iPhone Air',
  'iPhone18,5': 'iPhone 17e'
};

// ---------------------------------------------------------------------------
// Apple — screen signatures. Key = "<portrait CSS width>x<height>@<dpr>".
// Each candidate: [name, minIOSMajor, maxIOSMajor]. iOS jumped 18 → 26 in 2025.
// Source: Apple HIG "Layout" specifications + ios-resolution.com.
// ---------------------------------------------------------------------------

const IOS_ANY = 99;

export const IPHONE_SCREEN_SIGNATURES = {
  '320x568@2': [
    ['iPhone SE (1st gen)', 9, 15],
    ['iPhone 5s', 7, 12]
  ],
  '375x667@2': [
    ['iPhone SE (3rd gen)', 15, IOS_ANY],
    ['iPhone SE (2nd gen)', 13, 26],
    ['iPhone 8', 11, 16],
    ['iPhone 7', 10, 15],
    ['iPhone 6s', 9, 15],
    ['iPhone 6', 8, 12]
  ],
  '414x736@3': [
    ['iPhone 8 Plus', 11, 16],
    ['iPhone 7 Plus', 10, 15],
    ['iPhone 6s Plus', 9, 15],
    ['iPhone 6 Plus', 8, 12]
  ],
  '375x812@3': [
    ['iPhone 13 mini', 15, IOS_ANY],
    ['iPhone 12 mini', 14, 26],
    ['iPhone 11 Pro', 13, 26],
    ['iPhone XS', 12, 18],
    ['iPhone X', 11, 16]
  ],
  '360x780@3': [
    ['iPhone 13 mini', 15, IOS_ANY],
    ['iPhone 12 mini', 14, 26]
  ],
  '414x896@2': [
    ['iPhone 11', 13, 26],
    ['iPhone XR', 12, 18]
  ],
  '414x896@3': [
    ['iPhone 11 Pro Max', 13, 26],
    ['iPhone XS Max', 12, 18]
  ],
  '390x844@3': [
    ['iPhone 17e', 26, IOS_ANY],
    ['iPhone 16e', 18, IOS_ANY],
    ['iPhone 14', 16, IOS_ANY],
    ['iPhone 13', 15, IOS_ANY],
    ['iPhone 13 Pro', 15, IOS_ANY],
    ['iPhone 12', 14, 26],
    ['iPhone 12 Pro', 14, 26]
  ],
  '428x926@3': [
    ['iPhone 14 Plus', 16, IOS_ANY],
    ['iPhone 13 Pro Max', 15, IOS_ANY],
    ['iPhone 12 Pro Max', 14, 26]
  ],
  '393x852@3': [
    ['iPhone 16', 18, IOS_ANY],
    ['iPhone 15', 17, IOS_ANY],
    ['iPhone 15 Pro', 17, IOS_ANY],
    ['iPhone 14 Pro', 16, IOS_ANY]
  ],
  '430x932@3': [
    ['iPhone 16 Plus', 18, IOS_ANY],
    ['iPhone 15 Plus', 17, IOS_ANY],
    ['iPhone 15 Pro Max', 17, IOS_ANY],
    ['iPhone 14 Pro Max', 16, IOS_ANY]
  ],
  // 16 Pro / 17 / 17 Pro share one chassis. iOS jumped 18 → 26, and the UA
  // often still reports 18.x on a 17, so we keep the 17-series in the list
  // whenever this screen matches (not only when major >= 26).
  '402x874@3': [
    ['iPhone 17', 18, IOS_ANY],
    ['iPhone 17 Pro', 18, IOS_ANY],
    ['iPhone 16 Pro', 18, IOS_ANY]
  ],
  // Same chassis: 16 Pro Max and 17 Pro Max are indistinguishable by screen.
  '440x956@3': [
    ['iPhone 17 Pro Max', 18, IOS_ANY],
    ['iPhone 16 Pro Max', 18, IOS_ANY]
  ],
  '420x912@3': [['iPhone Air', 26, IOS_ANY]]
};

export const IPAD_SCREEN_SIGNATURES = {
  '768x1024@1': [
    ['iPad (1st/2nd gen)', 3, 9],
    ['iPad mini (1st gen)', 6, 9]
  ],
  '768x1024@2': [
    ['iPad (9.7", 3rd–6th gen)', 5, 16],
    ['iPad Air (1st/2nd gen)', 7, 15],
    ['iPad Pro 9.7"', 9, 16],
    ['iPad mini (2nd–5th gen)', 7, IOS_ANY]
  ],
  '810x1080@2': [['iPad 10.2" (7th–9th gen)', 13, IOS_ANY]],
  '820x1180@2': [
    ['iPad 11" (10th gen / A16)', 16, IOS_ANY],
    ['iPad Air 11" (M2/M3)', 17, IOS_ANY],
    ['iPad Air 10.9" (4th/5th gen)', 14, IOS_ANY]
  ],
  '834x1112@2': [
    ['iPad Air 10.5" (3rd gen)', 12, IOS_ANY],
    ['iPad Pro 10.5"', 10, IOS_ANY]
  ],
  '834x1194@2': [['iPad Pro 11" (1st–4th gen)', 12, IOS_ANY]],
  '834x1210@2': [['iPad Pro 11" (M4/M5)', 17, IOS_ANY]],
  '744x1133@2': [['iPad mini 8.3" (6th gen / A17 Pro)', 15, IOS_ANY]],
  '1024x1366@2': [
    ['iPad Pro 12.9"', 9, IOS_ANY],
    ['iPad Air 13" (M2/M3)', 17, IOS_ANY]
  ],
  '1032x1376@2': [['iPad Pro 13" (M4/M5)', 17, IOS_ANY]]
};

// ---------------------------------------------------------------------------
// Android — model code → marketing name (longest matching prefix wins).
// ---------------------------------------------------------------------------

const SAMSUNG = {
  // Galaxy S
  'SM-G930': 'Galaxy S7', 'SM-G935': 'Galaxy S7 edge',
  'SM-G950': 'Galaxy S8', 'SM-G955': 'Galaxy S8+',
  'SM-G960': 'Galaxy S9', 'SM-G965': 'Galaxy S9+',
  'SM-G970': 'Galaxy S10e', 'SM-G973': 'Galaxy S10', 'SM-G975': 'Galaxy S10+', 'SM-G977': 'Galaxy S10 5G', 'SM-G770': 'Galaxy S10 Lite',
  'SM-G980': 'Galaxy S20', 'SM-G981': 'Galaxy S20 5G', 'SM-G985': 'Galaxy S20+', 'SM-G986': 'Galaxy S20+ 5G', 'SM-G988': 'Galaxy S20 Ultra',
  'SM-G780': 'Galaxy S20 FE', 'SM-G781': 'Galaxy S20 FE 5G',
  'SM-G991': 'Galaxy S21', 'SM-G996': 'Galaxy S21+', 'SM-G998': 'Galaxy S21 Ultra', 'SM-G990': 'Galaxy S21 FE',
  'SM-S901': 'Galaxy S22', 'SM-S906': 'Galaxy S22+', 'SM-S908': 'Galaxy S22 Ultra',
  'SM-S911': 'Galaxy S23', 'SM-S916': 'Galaxy S23+', 'SM-S918': 'Galaxy S23 Ultra', 'SM-S711': 'Galaxy S23 FE',
  'SM-S921': 'Galaxy S24', 'SM-S926': 'Galaxy S24+', 'SM-S928': 'Galaxy S24 Ultra', 'SM-S721': 'Galaxy S24 FE',
  'SM-S931': 'Galaxy S25', 'SM-S936': 'Galaxy S25+', 'SM-S938': 'Galaxy S25 Ultra', 'SM-S937': 'Galaxy S25 Edge', 'SM-S731': 'Galaxy S25 FE',
  // Galaxy Note
  'SM-N950': 'Galaxy Note 8', 'SM-N960': 'Galaxy Note 9', 'SM-N970': 'Galaxy Note 10', 'SM-N975': 'Galaxy Note 10+', 'SM-N976': 'Galaxy Note 10+ 5G',
  'SM-N980': 'Galaxy Note 20', 'SM-N981': 'Galaxy Note 20 5G', 'SM-N985': 'Galaxy Note 20 Ultra', 'SM-N986': 'Galaxy Note 20 Ultra 5G',
  // Galaxy Z
  'SM-F700': 'Galaxy Z Flip', 'SM-F707': 'Galaxy Z Flip 5G', 'SM-F711': 'Galaxy Z Flip3', 'SM-F721': 'Galaxy Z Flip4', 'SM-F731': 'Galaxy Z Flip5', 'SM-F741': 'Galaxy Z Flip6', 'SM-F761': 'Galaxy Z Flip7', 'SM-F766': 'Galaxy Z Flip7 FE',
  'SM-F900': 'Galaxy Fold', 'SM-F916': 'Galaxy Z Fold2', 'SM-F926': 'Galaxy Z Fold3', 'SM-F936': 'Galaxy Z Fold4', 'SM-F946': 'Galaxy Z Fold5', 'SM-F956': 'Galaxy Z Fold6', 'SM-F966': 'Galaxy Z Fold7',
  // Galaxy A
  'SM-A015': 'Galaxy A01', 'SM-A013': 'Galaxy A01 Core', 'SM-A022': 'Galaxy A02', 'SM-A025': 'Galaxy A02s', 'SM-A035': 'Galaxy A03', 'SM-A032': 'Galaxy A03 Core', 'SM-A037': 'Galaxy A03s',
  'SM-A045': 'Galaxy A04', 'SM-A042': 'Galaxy A04e', 'SM-A047': 'Galaxy A04s', 'SM-A055': 'Galaxy A05', 'SM-A057': 'Galaxy A05s', 'SM-A065': 'Galaxy A06', 'SM-A066': 'Galaxy A06 5G', 'SM-A075': 'Galaxy A07',
  'SM-A105': 'Galaxy A10', 'SM-A107': 'Galaxy A10s', 'SM-A115': 'Galaxy A11', 'SM-A125': 'Galaxy A12', 'SM-A127': 'Galaxy A12', 'SM-A135': 'Galaxy A13', 'SM-A137': 'Galaxy A13', 'SM-A136': 'Galaxy A13 5G',
  'SM-A145': 'Galaxy A14', 'SM-A146': 'Galaxy A14 5G', 'SM-A155': 'Galaxy A15', 'SM-A156': 'Galaxy A15 5G', 'SM-A165': 'Galaxy A16', 'SM-A166': 'Galaxy A16 5G', 'SM-A176': 'Galaxy A17 5G',
  'SM-A205': 'Galaxy A20', 'SM-A202': 'Galaxy A20e', 'SM-A207': 'Galaxy A20s', 'SM-A215': 'Galaxy A21', 'SM-A217': 'Galaxy A21s', 'SM-A225': 'Galaxy A22', 'SM-A226': 'Galaxy A22 5G',
  'SM-A235': 'Galaxy A23', 'SM-A236': 'Galaxy A23 5G', 'SM-A245': 'Galaxy A24', 'SM-A256': 'Galaxy A25 5G', 'SM-A266': 'Galaxy A26 5G',
  'SM-A305': 'Galaxy A30', 'SM-A307': 'Galaxy A30s', 'SM-A315': 'Galaxy A31', 'SM-A325': 'Galaxy A32', 'SM-A326': 'Galaxy A32 5G', 'SM-A336': 'Galaxy A33 5G', 'SM-A346': 'Galaxy A34 5G', 'SM-A356': 'Galaxy A35 5G', 'SM-A366': 'Galaxy A36 5G',
  'SM-A405': 'Galaxy A40', 'SM-A415': 'Galaxy A41',
  'SM-A505': 'Galaxy A50', 'SM-A507': 'Galaxy A50s', 'SM-A515': 'Galaxy A51', 'SM-A516': 'Galaxy A51 5G', 'SM-A525': 'Galaxy A52', 'SM-A526': 'Galaxy A52 5G', 'SM-A528': 'Galaxy A52s 5G',
  'SM-A536': 'Galaxy A53 5G', 'SM-A546': 'Galaxy A54 5G', 'SM-A556': 'Galaxy A55 5G', 'SM-A566': 'Galaxy A56 5G',
  'SM-A705': 'Galaxy A70', 'SM-A707': 'Galaxy A70s', 'SM-A715': 'Galaxy A71', 'SM-A716': 'Galaxy A71 5G', 'SM-A725': 'Galaxy A72', 'SM-A736': 'Galaxy A73 5G',
  'SM-A805': 'Galaxy A80', 'SM-A908': 'Galaxy A90 5G',
  // Galaxy M / F
  'SM-M115': 'Galaxy M11', 'SM-M127': 'Galaxy M12', 'SM-M135': 'Galaxy M13', 'SM-M145': 'Galaxy M14', 'SM-M146': 'Galaxy M14 5G', 'SM-M156': 'Galaxy M15 5G', 'SM-M166': 'Galaxy M16 5G',
  'SM-M205': 'Galaxy M20', 'SM-M215': 'Galaxy M21', 'SM-M225': 'Galaxy M22', 'SM-M236': 'Galaxy M23 5G',
  'SM-M305': 'Galaxy M30', 'SM-M307': 'Galaxy M30s', 'SM-M315': 'Galaxy M31', 'SM-M317': 'Galaxy M31s', 'SM-M325': 'Galaxy M32', 'SM-M336': 'Galaxy M33 5G', 'SM-M346': 'Galaxy M34 5G', 'SM-M356': 'Galaxy M35 5G',
  'SM-M515': 'Galaxy M51', 'SM-M526': 'Galaxy M52 5G', 'SM-M536': 'Galaxy M53 5G', 'SM-M546': 'Galaxy M54 5G', 'SM-M556': 'Galaxy M55 5G',
  // Galaxy Tab
  'SM-T220': 'Galaxy Tab A7 Lite', 'SM-T225': 'Galaxy Tab A7 Lite LTE', 'SM-T500': 'Galaxy Tab A7', 'SM-T505': 'Galaxy Tab A7 LTE', 'SM-T510': 'Galaxy Tab A 10.1 (2019)', 'SM-T515': 'Galaxy Tab A 10.1 (2019) LTE',
  'SM-X200': 'Galaxy Tab A8', 'SM-X205': 'Galaxy Tab A8 LTE', 'SM-X110': 'Galaxy Tab A9', 'SM-X115': 'Galaxy Tab A9 LTE', 'SM-X210': 'Galaxy Tab A9+', 'SM-X216': 'Galaxy Tab A9+ 5G',
  'SM-P610': 'Galaxy Tab S6 Lite', 'SM-P615': 'Galaxy Tab S6 Lite LTE', 'SM-P613': 'Galaxy Tab S6 Lite (2022)', 'SM-P619': 'Galaxy Tab S6 Lite (2022) LTE',
  'SM-T870': 'Galaxy Tab S7', 'SM-T875': 'Galaxy Tab S7 LTE', 'SM-T970': 'Galaxy Tab S7+', 'SM-T976': 'Galaxy Tab S7+ 5G', 'SM-T730': 'Galaxy Tab S7 FE', 'SM-T736': 'Galaxy Tab S7 FE 5G',
  'SM-X700': 'Galaxy Tab S8', 'SM-X706': 'Galaxy Tab S8 5G', 'SM-X800': 'Galaxy Tab S8+', 'SM-X806': 'Galaxy Tab S8+ 5G', 'SM-X900': 'Galaxy Tab S8 Ultra', 'SM-X906': 'Galaxy Tab S8 Ultra 5G',
  'SM-X710': 'Galaxy Tab S9', 'SM-X716': 'Galaxy Tab S9 5G', 'SM-X810': 'Galaxy Tab S9+', 'SM-X816': 'Galaxy Tab S9+ 5G', 'SM-X910': 'Galaxy Tab S9 Ultra', 'SM-X916': 'Galaxy Tab S9 Ultra 5G', 'SM-X510': 'Galaxy Tab S9 FE', 'SM-X516': 'Galaxy Tab S9 FE 5G', 'SM-X610': 'Galaxy Tab S9 FE+',
  'SM-X920': 'Galaxy Tab S10 Ultra', 'SM-X926': 'Galaxy Tab S10 Ultra 5G', 'SM-X820': 'Galaxy Tab S10+', 'SM-X826': 'Galaxy Tab S10+ 5G'
};

const HUAWEI = {
  'ELS-': 'P40 Pro', 'ANA-': 'P40', 'JNY-': 'P40 lite', 'VOG-': 'P30 Pro', 'ELE-': 'P30', 'MAR-': 'P30 lite', 'CLT-': 'P20 Pro', 'EML-': 'P20', 'ANE-': 'P20 lite',
  'NOH-': 'Mate 40 Pro', 'OCE-': 'Mate 40', 'LIO-': 'Mate 30 Pro', 'TAS-': 'Mate 30', 'LYA-': 'Mate 20 Pro', 'HMA-': 'Mate 20', 'SNE-': 'Mate 20 lite',
  'ART-': 'nova 5T', 'JSC-': 'nova 7i', 'CDY-': 'nova 7 SE',
  'STK-': 'Y9 Prime 2019 / P smart Z', 'JKM-': 'Y9 2019', 'MRD-': 'Y6 2019', 'DUB-': 'Y7 2019', 'POT-': 'P smart 2019', 'AMN-': 'Y5 2019', 'DRA-': 'Y5 2018', 'ATU-': 'Y6 2018', 'LDN-': 'Y7 2018',
  'MED-': 'Y6p', 'AKA-': 'Y7p', 'PPA-': 'Y6p', 'AQM-': 'Y8p', 'FLA-': 'Y9 2018',
  'KOB2-': 'MatePad T 10', 'AGS3-': 'MatePad T 10s', 'AGS3K-': 'MatePad T 10s', 'AGR-': 'MatePad T 10', 'BAH3-': 'MatePad 10.4', 'DBY-': 'MatePad 11'
};

const XIAOMI = {
  'M2007J20CG': 'POCO X3 NFC', 'M2007J20CT': 'POCO X3', 'M2102J20SG': 'POCO X3 Pro', 'M2007J3SY': 'POCO X3 NFC',
  'M2101K6G': 'Redmi Note 10 Pro', 'M2101K6P': 'Redmi Note 10 Pro Max', 'M2101K7AG': 'Redmi Note 10', 'M2101K7BG': 'Redmi Note 10S', 'M2103K19G': 'Redmi Note 10 5G',
  'M2010J19SG': 'Redmi 9T', 'M2004J19C': 'Redmi 9', 'M2006C3LG': 'Redmi 9C', 'M2006C3MG': 'Redmi 9A', 'M2004J19PI': 'Redmi 9 Prime',
  'M2003J15SC': 'Redmi Note 9', 'M2003J6B2G': 'Redmi Note 9S', 'M2003J6A1G': 'Redmi Note 9 Pro', 'M2003J6B1I': 'Redmi Note 9 Pro Max',
  'M2007J17G': 'Redmi Note 9 Pro 5G', 'M2007J22G': 'Redmi Note 9T',
  'M2012K11AG': 'POCO F3', 'M2012K11G': 'Xiaomi Mi 11i', 'M2011K2G': 'Xiaomi Mi 11', 'M2102K1G': 'Xiaomi Mi 11 Ultra', 'M2101K9G': 'Xiaomi Mi 11 Lite', 'M2101K9AG': 'Xiaomi Mi 11 Lite 5G',
  '2109119DG': 'Xiaomi 11 Lite 5G NE', '2107113SG': 'Xiaomi 11T Pro', '21081111RG': 'Xiaomi 11T', '2201123G': 'Xiaomi 12', '2201122G': 'Xiaomi 12 Pro', '2203129G': 'Xiaomi 12 Lite',
  '2201116TG': 'Redmi Note 11 Pro 5G', '2201116SG': 'Redmi Note 11 Pro', '2201117TG': 'Redmi Note 11 Pro+ 5G', '2201117TY': 'Redmi Note 11S', '2201117SG': 'Redmi Note 11S 5G', '2201117TL': 'Redmi Note 11S',
  '2201116PG': 'POCO X4 Pro 5G', '21091116AG': 'Redmi Note 11', '2201117PG': 'POCO M4 Pro', '21091116UG': 'POCO M4 Pro 5G',
  '21061119AG': 'Redmi 10', '21061119DG': 'Redmi 10', '22011119UY': 'Redmi Note 11', '220333QAG': 'Redmi 10C', '220233L2G': 'Redmi 10A', '22041219G': 'Redmi Note 11E',
  '22101316G': 'Redmi Note 12', '22111317G': 'Redmi Note 12 5G', '2209116AG': 'Redmi Note 12 Pro 5G', '22101316UG': 'Redmi Note 12 Pro 4G', '22101316UP': 'Redmi Note 12 Pro 4G', '22101316UCP': 'Redmi Note 12 Pro',
  '23076RA4BG': 'Redmi 12', '23053RN02A': 'Redmi 12C', '2312DRA50G': 'Redmi Note 13', '23124RA7EO': 'Redmi Note 13 4G', '2312DRAABG': 'Redmi Note 13 Pro', '23117RA68G': 'Redmi Note 13 Pro 5G', '23090RA98G': 'Redmi Note 13 Pro+ 5G', '2311DRK48G': 'Redmi Note 13 Pro 4G',
  '24040RN64Y': 'Redmi Note 14', '24094RAD4G': 'Redmi Note 14 5G', '24115RA8EG': 'Redmi Note 14 Pro 4G', '2409BRN2CA': 'Redmi Note 14 Pro 5G', '24117RN76G': 'Redmi Note 14 Pro+ 5G',
  '2211133G': 'Xiaomi 13', '2210132G': 'Xiaomi 13 Pro', '2306EPN60G': 'Xiaomi 13T', '23078PND5G': 'Xiaomi 13T Pro', '2304FPN6DG': 'Xiaomi 13 Ultra', '2210129SG': 'Xiaomi 13 Lite',
  '23127PN0CG': 'Xiaomi 14', '23116PN5BC': 'Xiaomi 14 Pro', '2405CPX3DG': 'Xiaomi 14 Ultra', '2407FPN8EG': 'Xiaomi 14T', '2407FPN8ER': 'Xiaomi 14T', '24072PX77G': 'Xiaomi 14T Pro',
  '24129PN74G': 'Xiaomi 15', '25010PN30G': 'Xiaomi 15 Ultra',
  '22021211RG': 'Redmi Note 11 Pro+', '2207117BPG': 'POCO M5', '22071219CG': 'POCO M5s', '2201117PI': 'POCO M4 Pro', '22101320G': 'POCO X5 5G', '22101320I': 'POCO X5 5G', '22101320C': 'POCO X5 Pro 5G',
  '23049PCD8G': 'POCO F5', '23013PC75G': 'POCO F5 Pro', '2311DRK48I': 'POCO X6 Pro 5G', '23122PCD1G': 'POCO X6 5G', '23122PCD1I': 'POCO X6 5G', '2312FPCA6G': 'POCO X6 Pro 5G',
  '24069PC21G': 'POCO F6', '23113RKC6G': 'POCO F6 Pro', '2409FPCC4G': 'POCO X7', '24095PCADG': 'POCO X7 Pro', '2412DPC0AG': 'POCO F7', '24117RK2CG': 'POCO F7 Pro', '2412DPC0AI': 'POCO F7'
};

const OPPO_REALME = {
  'CPH2239': 'A54', 'CPH2185': 'A15', 'CPH2179': 'A15s', 'CPH2219': 'A74', 'CPH2211': 'A74 5G', 'CPH2195': 'Reno5 5G', 'CPH2145': 'Reno4 Pro', 'CPH2113': 'Reno4', 'CPH2127': 'A53', 'CPH2135': 'A53s', 'CPH2137': 'A53',
  'CPH2269': 'A16', 'CPH2273': 'A16s', 'CPH2275': 'A16', 'CPH2325': 'A76', 'CPH2333': 'A96', 'CPH2371': 'Reno8 5G', 'CPH2359': 'Reno8 Pro 5G', 'CPH2363': 'Reno8', 'CPH2375': 'A17', 'CPH2387': 'A17k', 'CPH2477': 'A77', 'CPH2385': 'A57',
  'CPH2461': 'A78 5G', 'CPH2495': 'A78', 'CPH2565': 'A58', 'CPH2557': 'A38', 'CPH2579': 'A18', 'CPH2481': 'Reno10 5G', 'CPH2525': 'Reno10 Pro 5G', 'CPH2531': 'Reno10 Pro 5G', 'CPH2591': 'Reno11 5G', 'CPH2599': 'Reno11 F 5G', 'CPH2607': 'Reno11 Pro 5G',
  'CPH2637': 'A60', 'CPH2669': 'A3 Pro', 'CPH2641': 'Reno12 5G', 'CPH2629': 'Reno12 Pro 5G', 'CPH2603': 'A79 5G', 'CPH2619': 'A79', 'CPH2691': 'A5 Pro', 'CPH2695': 'A5 Pro 5G',
  'RMX2185': 'realme C11', 'RMX2189': 'realme C11', 'RMX2180': 'realme C15', 'RMX3231': 'realme C11 (2021)', 'RMX3261': 'realme C21Y', 'RMX3201': 'realme C21', 'RMX3263': 'realme C21Y', 'RMX3269': 'realme C25Y',
  'RMX3085': 'realme 8', 'RMX3081': 'realme 8 Pro', 'RMX3241': 'realme 8 5G', 'RMX3151': 'realme 8i', 'RMX3363': 'realme GT Master Edition', 'RMX3381': 'realme 9 Pro 5G', 'RMX3392': 'realme 9 Pro+ 5G', 'RMX3491': 'realme 9', 'RMX3521': 'realme 9 4G',
  'RMX3501': 'realme C31', 'RMX3511': 'realme C31', 'RMX3627': 'realme C33', 'RMX3624': 'realme C33', 'RMX3630': 'realme C55', 'RMX3710': 'realme C53', 'RMX3760': 'realme C51', 'RMX3762': 'realme C51', 'RMX3830': 'realme C65', 'RMX3910': 'realme C61',
  'RMX3771': 'realme 11 Pro 5G', 'RMX3741': 'realme 11 Pro+ 5G', 'RMX3780': 'realme 11', 'RMX3781': 'realme 11 5G', 'RMX3871': 'realme 12 Pro 5G', 'RMX3840': 'realme 12 Pro+ 5G', 'RMX3999': 'realme 12+ 5G', 'RMX3890': 'realme 12x 5G',
  'RMX3933': 'realme 13 Pro 5G', 'RMX3931': 'realme 13 Pro+ 5G', 'RMX5001': 'realme 14 Pro 5G', 'RMX5002': 'realme 14 Pro+ 5G', 'RMX3990': 'realme C67', 'RMX3939': 'realme C63'
};

/** Prefix → vendor when we can't find a marketing name. */
const VENDOR_PREFIXES = [
  [/^SM-/i, 'Samsung'],
  [/^GT-/i, 'Samsung'],
  [/^CPH\d/i, 'OPPO'],
  [/^RMX\d/i, 'realme'],
  [/^V\d{4}[A-Z]?$/i, 'vivo'],
  [/^Infinix/i, 'Infinix'],
  [/^TECNO/i, 'TECNO'],
  [/^itel/i, 'itel'],
  [/^Pixel/i, 'Google'],
  [/^Nokia/i, 'Nokia'],
  [/^moto|^XT\d{4}/i, 'Motorola'],
  [/^ONEPLUS|^NE\d{4}|^LE\d{4}|^KB\d{4}/i, 'OnePlus'],
  [/^Redmi|^POCO|^Mi |^Xiaomi|^M2\d{3}|^2\d{6,9}[A-Z]/i, 'Xiaomi'],
  [/^[A-Z]{3}\d?-[A-Z]{1,2}\d{1,2}[A-Z]?$/, 'Huawei / Honor'],
  [/^Lenovo/i, 'Lenovo'],
  [/^HTC/i, 'HTC'],
  [/^LG-|^LM-/i, 'LG'],
  [/^SO-|^XQ-|^Xperia/i, 'Sony']
];

const lookupPrefix = (table, code) => {
  const upper = code.toUpperCase();
  let best = null;
  Object.keys(table).forEach((prefix) => {
    if (upper.startsWith(prefix.toUpperCase()) && (!best || prefix.length > best.length)) best = prefix;
  });
  return best ? table[best] : null;
};

const guessVendor = (code) => {
  for (const [re, vendor] of VENDOR_PREFIXES) {
    if (re.test(code)) return vendor;
  }
  return null;
};

const dedupeVendor = (vendor, name) => {
  if (!vendor) return name;
  if (!name) return vendor;
  return name.toLowerCase().startsWith(vendor.toLowerCase()) ? name : `${vendor} ${name}`;
};

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

const parseMajor = (version) => {
  if (version === null || version === undefined) return null;
  const m = String(version).match(/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
};

const isApple = ({ osName, userAgent, vendor, platform, maxTouchPoints }) => {
  const os = (osName || '').toLowerCase();
  const ua = userAgent || '';
  if (os === 'ios' || os === 'ipados') return true;
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  if ((vendor || '').toLowerCase() === 'apple' && /mac/i.test(platform || '') && (maxTouchPoints || 0) > 1) return true;
  if (/Macintosh/.test(ua) && (maxTouchPoints || 0) > 1) return true; // iPadOS 13+ "desktop" UA
  return false;
};

const isIpad = ({ osName, userAgent, platform, maxTouchPoints, deviceType, screenWidth, screenHeight }) => {
  if ((osName || '').toLowerCase() === 'ipados') return true;
  if (/iPad/.test(userAgent || '')) return true;
  if (/iPhone|iPod/.test(userAgent || '')) return false;
  if (/Macintosh/.test(userAgent || '') && (maxTouchPoints || 0) > 1) return true;
  if (/mac/i.test(platform || '') && (maxTouchPoints || 0) > 1) return true;
  if (deviceType === 'tablet') return true;
  const w = Math.min(screenWidth || 0, screenHeight || 0);
  return w >= 700; // any iOS device this wide in CSS pt is an iPad
};

const appleScreenCandidates = (input, ipad) => {
  const { screenWidth, screenHeight, dpr, osVersion } = input;
  if (!screenWidth || !screenHeight) return null;
  const table = ipad ? IPAD_SCREEN_SIGNATURES : IPHONE_SCREEN_SIGNATURES;
  const w = Math.min(screenWidth, screenHeight);
  const h = Math.max(screenWidth, screenHeight);
  const ratio = dpr || (ipad ? 2 : 3);

  const keys = [`${w}x${h}@${ratio}`];
  // Some collectors report physical pixels; try dividing by dpr as well.
  if (ratio > 1) keys.push(`${Math.round(w / ratio)}x${Math.round(h / ratio)}@${ratio}`);
  // Landscape iPad UA sometimes swaps; and dpr may come as 2.999
  const roundedRatio = Math.round(ratio);
  if (roundedRatio !== ratio) keys.push(`${w}x${h}@${roundedRatio}`);

  let list = null;
  for (const key of keys) {
    if (table[key]) {
      list = table[key];
      break;
    }
  }
  if (!list) return null;

  const major = parseMajor(osVersion);
  let filtered = list;
  if (major) {
    const narrowed = list.filter(([, min, max]) => major >= min && major <= max);
    if (narrowed.length) filtered = narrowed;
  }
  return rankAppleCandidates(filtered.map(([name]) => name), major);
};

/**
 * Keep table order (newer first) on iOS 26+. On an iOS 18-era UA the 16-series
 * is the safer lead when the list is only 16/17 chassis twins — the 17-series
 * stay as alternates because they share the same screen.
 */
export const rankAppleCandidates = (names, major) => {
  if (!names || names.length < 2) return names;
  if (major && major >= 26) return [...names];
  const only1617 = names.every((n) => /iPhone 1[67]/.test(n));
  if (!only1617) return [...names];
  return names
    .map((name, index) => ({ name, index }))
    .sort((a, b) => {
      const a17 = /iPhone 17/.test(a.name) ? 1 : 0;
      const b17 = /iPhone 17/.test(b.name) ? 1 : 0;
      if (a17 !== b17) return a17 - b17;
      return a.index - b.index;
    })
    .map((row) => row.name);
};

const joinCandidates = (names) => {
  if (!names || names.length === 0) return null;
  if (names.length === 1) return names[0];
  const shown = names.slice(0, 3);
  const rest = names.length - shown.length;
  const base = shown.length === 2 ? `${shown[0]} or ${shown[1]}` : `${shown.slice(0, -1).join(', ')} or ${shown[shown.length - 1]}`;
  return rest > 0 ? `${base} (+${rest})` : base;
};

/**
 * @param {Object} input
 * @param {string} [input.vendor]        parsed.device.vendor
 * @param {string} [input.model]         parsed.device.model or userAgentData.model
 * @param {string} [input.userAgent]     raw UA
 * @param {string} [input.osName]        parsed.os.name
 * @param {string} [input.osVersion]     parsed.os.version
 * @param {number} [input.screenWidth]   client.screen.width (CSS px)
 * @param {number} [input.screenHeight]  client.screen.height
 * @param {number} [input.dpr]           client.viewport.devicePixelRatio
 * @param {number} [input.maxTouchPoints]
 * @param {string} [input.platform]      navigator.platform
 * @param {string} [input.deviceType]    mobile | tablet | desktop | smarttv
 * @returns {{ name: string|null, vendor: string|null, code: string|null, candidates: string[], confidence: 'exact'|'code'|'inferred'|'unknown', method: string|null }}
 */
export const resolveDeviceModel = (input = {}) => {
  const ua = input.userAgent || '';
  const result = { name: null, vendor: null, code: null, candidates: [], confidence: 'unknown', method: null };

  // 1. Apple hardware identifier leaked by in-app browsers (FBDV/iPhone18,2 …)
  const hw = ua.match(/\b(iPhone\d{1,2},\d{1,2}|iPad\d{1,2},\d{1,2})\b/);
  if (hw && APPLE_HARDWARE_IDS[hw[1]]) {
    return {
      ...result,
      name: APPLE_HARDWARE_IDS[hw[1]],
      vendor: 'Apple',
      code: hw[1],
      candidates: [APPLE_HARDWARE_IDS[hw[1]]],
      confidence: 'exact',
      method: 'hardware-id'
    };
  }

  // 2. Apple: infer from screen signature
  if (isApple(input)) {
    const ipad = isIpad(input);
    const family = ipad ? 'iPad' : 'iPhone';
    const candidates = appleScreenCandidates(input, ipad);
    if (candidates && candidates.length) {
      return {
        ...result,
        name: joinCandidates(candidates),
        vendor: 'Apple',
        code: hw ? hw[1] : null,
        candidates,
        confidence: 'inferred',
        method: 'screen'
      };
    }
    return { ...result, name: family, vendor: 'Apple', candidates: [family], confidence: 'unknown', method: null };
  }

  // 3. Android & others: model code from UA / client hints
  const code = (input.model || '').trim();
  if (!code) {
    return input.vendor ? { ...result, name: input.vendor, vendor: input.vendor } : result;
  }

  const vendorHint = input.vendor || guessVendor(code);
  let marketing = null;
  let vendor = vendorHint;

  if (/^SM-|^GT-/i.test(code)) {
    marketing = lookupPrefix(SAMSUNG, code);
    vendor = 'Samsung';
    if (!marketing) {
      const series = code.toUpperCase().match(/^SM-([A-Z])/);
      const seriesName = series
        ? { G: 'Galaxy S', S: 'Galaxy S', N: 'Galaxy Note', F: 'Galaxy Z', A: 'Galaxy A', M: 'Galaxy M', T: 'Galaxy Tab', X: 'Galaxy Tab', P: 'Galaxy Tab', J: 'Galaxy J' }[series[1]]
        : null;
      if (seriesName) marketing = `${seriesName} series`;
    }
  } else if (/^CPH\d/i.test(code)) {
    marketing = lookupPrefix(OPPO_REALME, code);
    vendor = vendor || 'OPPO';
  } else if (/^RMX\d/i.test(code)) {
    marketing = lookupPrefix(OPPO_REALME, code);
    vendor = 'realme';
  } else if (/^M2\d{3}|^2\d{6,9}[A-Z]/i.test(code)) {
    marketing = lookupPrefix(XIAOMI, code);
    vendor = vendor || 'Xiaomi';
  } else if (/^[A-Z]{3}\d?-[A-Z]{1,2}\d{1,2}[A-Z]?$/.test(code)) {
    marketing = lookupPrefix(HUAWEI, code);
    vendor = input.vendor || 'Huawei';
  }

  if (marketing) {
    const name = dedupeVendor(vendor, marketing);
    return { ...result, name: `${name} (${code})`, vendor, code, candidates: [name], confidence: 'code', method: 'model-code' };
  }

  // Marketing-style names already in the UA ("Pixel 8 Pro", "Redmi Note 12", "moto g54 5G", "Infinix X6816")
  const name = dedupeVendor(vendor, code);
  const looksMarketing = /\s/.test(code) || /^(Pixel|Redmi|POCO|moto|Nokia|Infinix|TECNO|itel|Xperia|Nord)/i.test(code);
  return {
    ...result,
    name,
    vendor: vendor || null,
    code,
    candidates: [name],
    confidence: looksMarketing ? 'code' : 'unknown',
    method: 'model-code'
  };
};

export default resolveDeviceModel;
